import { filter, isFunction, size } from "lodash";
import {
  AudioLines,
  FileIcon,
  GalleryVertical,
  Plus,
  Sparkle,
  Tag,
} from "lucide-react";
import { FC, useCallback, useMemo, useState } from "react";
import { useParams } from "react-router";

import BuilderIcon from "@/assets/logos/builder.svg?react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useUpdateSKUMutation } from "@/data/mutations";
import { SKUPageContentTab, SKUPageContentTabType } from "@/data/types";
import { useCMSStore } from "@/hooks/use-cms-store";
import {
  CREATABLE_TAB_TYPES,
  MAX_TABS_PER_PAGE,
  MAX_TABS_PER_TYPE,
} from "@/lib/cms";

export const CMSAddTabButton = () => {
  const [isOpen, setOpen] = useState(false);
  const { skuId } = useParams<{ skuId: string }>();
  const pageContent = useCMSStore.use.pageContent();
  const onAdd = useCMSStore.use.onAddTab();

  const updateSKUMutation = useUpdateSKUMutation(skuId!);

  const handleAddTab = useCallback(
    (type: SKUPageContentTabType) => {
      onAdd(type);
      setOpen(false);
    },
    [onAdd],
  );

  const canAddTab = useMemo(() => {
    if (updateSKUMutation.isPending) {
      return false;
    }

    return size(pageContent?.data) < MAX_TABS_PER_PAGE;
  }, [pageContent?.data, updateSKUMutation.isPending]);

  const canAddTabByType = useCallback(
    (type: SKUPageContentTabType) => {
      const existing = filter(
        pageContent?.data,
        (tab: SKUPageContentTab) => tab.type === type,
      );

      return size(existing) < MAX_TABS_PER_TYPE[type];
    },
    [pageContent?.data],
  );

  return (
    <Popover open={isOpen} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className="mt-6"
          disabled={!canAddTab || updateSKUMutation.isPending}
        >
          <Plus className="size-4" />
          Add tab
        </Button>
      </PopoverTrigger>
      <PopoverContent side="top" align="start" sideOffset={8} className="w-90">
        <div>
          <h4 className="font-medium text-base">Add tab</h4>
          <p className="text-sm text-muted-foreground">
            Choose a type of tab to add
          </p>
          <div className="space-y-2 mt-2">
            {CREATABLE_TAB_TYPES.map((type: SKUPageContentTabType) => {
              const canAddTabType = canAddTabByType(type);

              return (
                <TabPickerButton
                  key={type}
                  type={type}
                  onClick={handleAddTab}
                  disabled={!canAddTabType}
                />
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

interface TabPickerButtonProps {
  type: SKUPageContentTabType;
  onClick: (type: SKUPageContentTabType) => void;
  disabled?: boolean;
}

export const TabPickerButton: FC<TabPickerButtonProps> = ({
  type,
  onClick,
  disabled,
}) => {
  const handleClick = useCallback(() => {
    if (isFunction(onClick)) {
      onClick(type);
    }
  }, [onClick, type]);

  return (
    <Button
      variant="outline"
      className="w-full justify-start"
      onClick={handleClick}
      disabled={disabled}
    >
      {TabPickerTypeMetadata[type].icon}
      {TabPickerTypeMetadata[type].title}
      <span className="text-muted-foreground font-normal -ml-1">
        {TabPickerTypeMetadata[type].description}
      </span>
    </Button>
  );
};

const TabPickerTypeMetadata = {
  [SKUPageContentTabType.PRODUCT_TAB]: {
    title: "Product",
    description: "View digital product information",
    icon: <Tag className="size-4" />,
  },
  [SKUPageContentTabType.MUSIC_TAB]: {
    title: "Audio",
    description: "Insert mp3 files to make a playlist",
    icon: <AudioLines className="size-4" />,
  },
  [SKUPageContentTabType.GALLERY_TAB]: {
    title: "Gallery",
    description: "Add images to a gallery",
    icon: <GalleryVertical className="size-4" />,
  },
  [SKUPageContentTabType.BUILDER_IO_TAB]: {
    title: "Builder.io",
    description: "Plug in a page built in Builder.io",
    icon: <BuilderIcon className="size-4" />,
  },
  [SKUPageContentTabType.UNIQUE_TAB]: {
    title: "Unique",
    description: "Add unique content per tag",
    icon: <Sparkle className="size-4" />,
  },
  [SKUPageContentTabType.WEBPAGE_TAB]: {
    title: "Webpage",
    description: "Embed a webpage",
    icon: <FileIcon className="size-4" />,
  },
} as const;
